import { PROFESSIONS } from './tasks.js';
import { MAX_CREW, MIN_CREW, professionCapacity, seatHeld, seatOf } from './state.js';
import { osloDay } from './boxoffice.js';
import { ALL_PLACES, PLACE_MOVE_MAX_MM, ROW_SPACING_MAX_MM, SIDE_PLACES, TABLE_SEATS } from './screens.js';
import type { PlaceShift } from './screens.js';
import type { Profession, Seat, ShowState } from './types.js';

/**
 * Seating, and the shape of the actual room.
 *
 *        ── TV ──
 *        ─ Marek ─
 *      ─ bar station ─
 *      01  ▓table▓  02      row 1 — nearest the screen
 *      03  ▓table▓  04      row 2
 *      05  ▓table▓  06      row 3
 *      07  ▓table▓  08      row 4
 *      09  ▓table▓  10      row 5
 *        11 ╲____╱ 12       the round far end, at 45° (row 6)
 *
 * One long table running away from the screen: five rows of two facing each other along
 * the sides, and two at its round far end (Marek, 2026-09-25; screens.ts `placeOnTable`).
 * Odd seats sit on the left, even on the right, and the numbering climbs away from the
 * TV — which is what makes a short table fill correctly. Six guests occupy seats 1–6,
 * the three rows closest to the screen; the far end simply stays empty. Nobody is ever
 * seated at the back of an empty room. How many places are laid follows the box office
 * (`crewFromBookings`).
 *
 * This replaces an earlier model that treated the twelve as a loop, where seat 1 and
 * seat 12 counted as neighbours. In this room they are at opposite ends of the table,
 * as far apart as two guests can be.
 */

/** Seats per row. The table is two-sided. */
const PER_ROW = 2;

/** Rows between two crew on the same post. Facing seats share a row, so they are 0. */
export const MIN_POST_SPACING = 2;

/** Which row a seat is in. Row 1 is closest to the screen. */
export function seatRow(seat: number): number {
  return Math.ceil(seat / PER_ROW);
}

/** Which side of the table a seat is on, looking from the screen. */
export function seatSide(seat: number): 'left' | 'right' {
  return seat % PER_ROW === 1 ? 'left' : 'right';
}

/**
 * How far apart two seats are, in rows.
 *
 * Rows rather than seat numbers because the room is what matters: two guests facing
 * each other across the table are as close as two guests can be, however far apart
 * their seat numbers happen to run.
 */
export function seatDistance(a: number, b: number): number {
  return Math.abs(seatRow(a) - seatRow(b));
}

export interface ProfessionOption {
  profession: Profession;
  available: boolean;
  /** How many of this post are still unclaimed. */
  remaining: number;
  reason: string | null;
  /**
   * Why it is closed, for the phone's own words: full; its other holder sits too close; or it is
   * taken and another post has nobody yet (every post is held once before any is held twice).
   */
  closed: 'full' | 'close' | 'first' | null;
}

/** What the seating rules read: the phones and the consoles have these too. */
export type SeatingState = Pick<ShowState, 'crewSize'> & { seats: readonly Pick<Seat, 'seat' | 'profession'>[] };

/**
 * What this seat may choose. While any post is held by nobody, only those posts are open, so every
 * era's six games have a player before any game has two (Marek, 2026-09-28: a post nobody took
 * left its game unplayed all evening). After that, posts already held by a nearby seat are closed,
 * unless closing them would leave nothing at all.
 */
export function professionOptionsFor(state: SeatingState, seat: number): ProfessionOption[] {
  const capacity = professionCapacity(state.crewSize);
  const holdersOf = (profession: Profession) => state.seats.filter((s) => s.profession === profession && s.seat !== seat);
  const firstRound = PROFESSIONS.some((p) => holdersOf(p).length === 0);

  const options = PROFESSIONS.map((profession): ProfessionOption => {
    const holders = holdersOf(profession);
    const remaining = capacity - holders.length;
    if (remaining <= 0) {
      return { profession, available: false, remaining: 0, reason: 'fully crewed', closed: 'full' };
    }
    if (firstRound && holders.length > 0) {
      return { profession, available: false, remaining, reason: 'taken — every post is held once first', closed: 'first' };
    }
    const tooClose = holders.find((h) => seatDistance(h.seat, seat) < MIN_POST_SPACING);
    if (tooClose) {
      return {
        profession,
        available: false,
        remaining,
        reason: `already crewed at seat ${String(tooClose.seat).padStart(2, '0')} — sit apart`,
        closed: 'close',
      };
    }
    return { profession, available: true, remaining, reason: null, closed: null };
  });

  // Never strand a guest: if the spacing rule closed everything, reopen the posts
  // that still have room.
  if (!options.some((o) => o.available)) {
    for (const option of options) {
      if (option.remaining > 0 && option.closed !== 'first') {
        option.available = true;
        option.reason = 'seated close — the only post left';
        option.closed = null;
      }
    }
  }

  return options;
}

/**
 * The post for a guest the host adds at the door (Marek, 2026-09-29: ADD PLAYER "automatically
 * opens a new call sign and profession"): what the phone would offer them first — a post nobody
 * holds while there is one, then the one with the most room, then the one whose other holder sits
 * furthest away. Null only when every post is full.
 */
export function nextOpenPost(state: SeatingState, seat: number): Profession | null {
  const open = professionOptionsFor(state, seat).filter((o) => o.available);
  const nearest = (profession: Profession) =>
    Math.min(
      99,
      ...state.seats.filter((s) => s.profession === profession && s.seat !== seat).map((s) => seatDistance(s.seat, seat)),
    );
  const best = [...open].sort((a, b) => b.remaining - a.remaining || nearest(b.profession) - nearest(a.profession))[0];
  return best?.profession ?? null;
}

/** Posts whose two holders ended up too close, for the console to flag. */
export function seatingWarnings(state: ShowState): { profession: Profession; seats: number[] }[] {
  const out: { profession: Profession; seats: number[] }[] = [];
  for (const profession of PROFESSIONS) {
    const seats = state.seats.filter((s) => s.profession === profession).map((s) => s.seat);
    for (let i = 0; i < seats.length; i++) {
      for (let j = i + 1; j < seats.length; j++) {
        if (seatDistance(seats[i]!, seats[j]!) < MIN_POST_SPACING) {
          out.push({ profession, seats: [seats[i]!, seats[j]!] });
        }
      }
    }
  }
  return out;
}

/**
 * Where the posts fall on paper.
 *
 * Seat n and seat n+6 hold the same post, which in this room is always three rows
 * apart — the two crew on a post are never facing each other, never shoulder to
 * shoulder, and never diagonal. It holds at every table size: at seven guests the one
 * doubled post sits in rows 1 and 4, at twelve every post does.
 *
 * Guests still pick their own post when they scan in; this is the plan, and
 * `professionOptionsFor` is what actually enforces the spacing at the table.
 */
export function suggestedSeating(crewSize: number): { seat: number; profession: Profession }[] {
  const posts = [...PROFESSIONS];
  return Array.from({ length: crewSize }, (_, i) => ({
    seat: i + 1,
    profession: posts[i % posts.length]!,
  }));
}

export function isSeatedApart(state: ShowState): boolean {
  return seatingWarnings(state).length === 0;
}

export { seatOf };

/** An evening as the box office's console lists it: when it starts and how many seats are held or paid. */
export interface BookedEvening {
  startsAt: string;
  taken: number;
}

/**
 * How many places to lay tonight, from the box office (Marek, 2026-09-25): the seats held or paid
 * for today's evening, kept to the crew sizes the show plays (4 to 12), and never fewer than the
 * highest seat already taken at the door. Null when the box office has no evening today.
 */
export function crewFromBookings(
  evenings: readonly BookedEvening[],
  now: number,
  highestClaimed = 0,
): { crew: number; booked: number; startsAt: string } | null {
  const today = osloDay(now);
  const tonight = evenings
    .filter((e) => osloDay(Date.parse(e.startsAt)) === today)
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))[0];
  if (!tonight) return null;
  const booked = Math.max(0, Math.round(tonight.taken));
  const crew = Math.min(MAX_CREW, Math.max(MIN_CREW, booked, highestClaimed));
  return { crew, booked, startsAt: tonight.startsAt };
}

// ---------------------------------------------------------------------------
// Tonight's table (Marek, 2026-10-06: "the source of truth should be the box office app on mobile
// where you choose how many guests are coming and select on the already built map function which
// exact seats are open"). The box office keeps one for each evening; the phone at the door edits
// it, and the Mac follows it — the room's players, the places laid, the plates and coasters lit and
// where each place is — and writes its own changes back through it (ADD PLAYER's place, the mapping
// window's picture of the table), so every screen agrees.

export interface TonightsTable {
  /** The evening it is for: its sitting's id, or `day:YYYY-MM-DD` on a day with no evening on sale. */
  key: string;
  sittingId: string;
  /** The Oslo day it is for. */
  day: string;
  /** How many players: the crew the room is asked for. */
  players: number;
  /** The places laid, 1–12 (`TablePlace.place`), in the order the seats take them (`seatOrder`). */
  openPlaces: number[];
  /** Places the Mac opened by itself for a crew larger than the places laid (`placesForCrew`). */
  added: number[];
  /**
   * Each place moved as a whole for the evening — its plate, coaster and words together — in
   * millimetres (`placeMoveOf`). A place not listed is where the mapping put it.
   */
  moves: Partial<Record<number, PlaceShift>>;
  /** Every gap between neighbouring rows along the sides grown (or, below 0, shrunk) by this much (`spacingAlong`). */
  spacing: number;
  /** Set by hand — at the door or in the mapping window: the bookings no longer set the players tonight. */
  byHand: boolean;
  /** Until when a phone's map holds the name tags steady on the table (ms since 1970); 0 for not. */
  holdTags: number;
  /** The Mac's last word on it, when it could not do all of it: the room kept another number of players, say. */
  note: string;
  /** Who changed it last, when, and how many changes it has had. */
  by: 'phone' | 'mac' | '';
  at: number;
  rev: number;
}

/** What a change to tonight's table may say; anything left out stays as it is. */
export interface TablePatch {
  players?: number;
  openPlaces?: readonly number[];
  added?: readonly number[];
  /** Places moved, each to where it is given (not by how much more); null puts one back. */
  moves?: Partial<Record<number, PlaceShift | null>>;
  /** Every place put back first (BACK TO DEFAULT), before `moves`. */
  clearMoves?: boolean;
  spacing?: number;
  byHand?: boolean;
  /** True holds the name tags on the table for `TABLE_HOLD_MS` from now (a phone's map is open); false lets go. */
  holdTags?: boolean;
  note?: string;
  /** Only if the table is still at this count of changes: the Mac's first word on an evening never overwrites a phone's. */
  ifRev?: number;
}

/** A phone's map holds the tags this long, and says again well before it runs out while it stays open. */
export const TABLE_HOLD_MS = 120_000;

const NOTE_MAX = 160;

/** Each place 1–12 once, in the order given. */
const placesOnly = (list: unknown): number[] => {
  const out: number[] = [];
  if (!Array.isArray(list)) return out;
  for (const p of list) if (typeof p === 'number' && ALL_PLACES.includes(p) && !out.includes(p)) out.push(p);
  return out;
};

const mmOf = (n: unknown, max: number): number =>
  typeof n === 'number' && Number.isFinite(n) ? Math.round(Math.max(-max, Math.min(max, n))) : 0;

/** Moves checked: places 1–12, whole millimetres within the limit, none left at nothing. */
function movesOf(raw: unknown): Partial<Record<number, PlaceShift>> {
  const out: Partial<Record<number, PlaceShift>> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const place of ALL_PLACES) {
    const s = (raw as Record<string, { along?: unknown; across?: unknown } | null | undefined>)[String(place)];
    if (!s || typeof s !== 'object') continue;
    const along = mmOf(s.along, PLACE_MOVE_MAX_MM);
    const across = mmOf(s.across, PLACE_MOVE_MAX_MM);
    if (along !== 0 || across !== 0) out[place] = { along, across };
  }
  return out;
}

const playersOf = (n: unknown, fallback: number): number =>
  typeof n === 'number' && Number.isFinite(n) ? Math.min(MAX_CREW, Math.max(MIN_CREW, Math.round(n))) : fallback;

/**
 * A new evening's table: laid and placed as the last one was (the same room, the same plates), its
 * players as many as its places, nothing yet set by hand.
 */
export function freshTable(at: { key: string; sittingId: string; day: string }, last: TonightsTable | null = null): TonightsTable {
  const openPlaces = last ? [...last.openPlaces] : [...SIDE_PLACES];
  return {
    key: at.key,
    sittingId: at.sittingId,
    day: at.day,
    players: playersOf(openPlaces.length, MIN_CREW),
    openPlaces,
    added: last ? last.added.filter((p) => openPlaces.includes(p)) : [],
    moves: last ? movesOf(last.moves) : {},
    spacing: last ? mmOf(last.spacing, ROW_SPACING_MAX_MM) : 0,
    byHand: false,
    holdTags: 0,
    note: '',
    by: '',
    at: 0,
    rev: 0,
  };
}

/** A table as stored or sent, checked field by field; null when it is not one at all. */
export function tableRecordOr(raw: unknown): TonightsTable | null {
  const t = raw as Partial<Record<keyof TonightsTable, unknown>> | null;
  if (!t || typeof t !== 'object' || typeof t.key !== 'string' || !t.key) return null;
  const openPlaces = placesOnly(t.openPlaces);
  const num = (n: unknown) => (typeof n === 'number' && Number.isFinite(n) ? n : 0);
  return {
    key: t.key.slice(0, 60),
    sittingId: typeof t.sittingId === 'string' ? t.sittingId.slice(0, 60) : '',
    day: typeof t.day === 'string' ? t.day.slice(0, 10) : '',
    players: playersOf(t.players, playersOf(openPlaces.length, MIN_CREW)),
    openPlaces,
    added: placesOnly(t.added).filter((p) => openPlaces.includes(p)),
    moves: movesOf(t.moves),
    spacing: mmOf(t.spacing, ROW_SPACING_MAX_MM),
    byHand: t.byHand === true,
    holdTags: Math.max(0, num(t.holdTags)),
    note: typeof t.note === 'string' ? t.note.slice(0, NOTE_MAX) : '',
    by: t.by === 'phone' || t.by === 'mac' ? t.by : '',
    at: Math.max(0, num(t.at)),
    rev: Math.max(0, Math.round(num(t.rev))),
  };
}

/**
 * Tonight's table with a change made: checked, counted one up, stamped with who and when. A change
 * that asks for the impossible — players outside 4–12, more players than places can hold — is
 * refused whole, with the reason. A change from the phone or of the places clears the Mac's last word,
 * which no longer applies.
 */
export function changeTable(
  table: TonightsTable,
  patch: TablePatch,
  by: 'phone' | 'mac',
  now: number,
): { ok: true; table: TonightsTable } | { ok: false; error: string } {
  const p = (patch ?? {}) as Partial<Record<keyof TablePatch, unknown>>;
  if (typeof p.ifRev === 'number' && p.ifRev !== table.rev) return { ok: false, error: 'tonight’s table has changed since: read it again' };
  const next: TonightsTable = { ...table, moves: { ...table.moves } };
  if (p.players !== undefined) {
    if (typeof p.players !== 'number' || !Number.isFinite(p.players)) return { ok: false, error: 'players must be a number' };
    const players = Math.round(p.players);
    if (players < MIN_CREW || players > MAX_CREW) return { ok: false, error: `a crew is ${MIN_CREW} to ${MAX_CREW} players` };
    next.players = players;
  }
  if (p.openPlaces !== undefined) {
    if (!Array.isArray(p.openPlaces)) return { ok: false, error: 'the places must be a list' };
    next.openPlaces = placesOnly(p.openPlaces);
    next.added = next.added.filter((x) => next.openPlaces.includes(x));
  }
  if (p.added !== undefined) next.added = placesOnly(p.added).filter((x) => next.openPlaces.includes(x));
  if (p.clearMoves === true) next.moves = {};
  if (p.moves !== undefined) {
    if (!p.moves || typeof p.moves !== 'object') return { ok: false, error: 'the moves must be places' };
    const given = p.moves as Record<string, PlaceShift | null>;
    for (const place of ALL_PLACES) {
      if (!(String(place) in given)) continue;
      const s = given[String(place)];
      if (s === null) delete next.moves[place];
      else if (s) next.moves[place] = s;
    }
    next.moves = movesOf(next.moves);
  }
  if (p.spacing !== undefined) next.spacing = mmOf(p.spacing, ROW_SPACING_MAX_MM);
  if (typeof p.byHand === 'boolean') next.byHand = p.byHand;
  if (typeof p.holdTags === 'boolean') next.holdTags = p.holdTags ? now + TABLE_HOLD_MS : 0;
  if (typeof p.note === 'string') next.note = p.note.slice(0, NOTE_MAX);
  else if (by === 'phone' || p.players !== undefined || p.openPlaces !== undefined) next.note = '';
  if (next.players > TABLE_SEATS) return { ok: false, error: `the table has ${TABLE_SEATS} places` };
  next.by = by;
  next.at = now;
  next.rev = table.rev + 1;
  return { ok: true, table: next };
}

/** Whether two tables lay and place the same: the players, the places in their order, the moves and the spacing. */
export function sameTable(
  a: Pick<TonightsTable, 'players' | 'openPlaces' | 'added' | 'moves' | 'spacing'>,
  b: Pick<TonightsTable, 'players' | 'openPlaces' | 'added' | 'moves' | 'spacing'>,
): boolean {
  return (
    a.players === b.players &&
    a.spacing === b.spacing &&
    a.openPlaces.join(',') === b.openPlaces.join(',') &&
    a.added.join(',') === b.added.join(',') &&
    JSON.stringify(movesOf(a.moves)) === JSON.stringify(movesOf(b.moves))
  );
}

/** A seat as the room's state says it, from either side of the wire. */
interface SeatSaid {
  seat: number;
  token?: string | null;
  claimed?: boolean;
  heldAt?: number | null;
}

/**
 * Why the room would keep its crew rather than take `size` players — the same rules as its own
 * `setCrewSize`: 4 to 12, never below a seat already claimed or being checked in at the door (after
 * departure too, since 2026-10-24: late guests). Null when it would take it.
 */
export function crewRefusal(
  state: { chapter?: number; seats?: readonly SeatSaid[] | null } | null | undefined,
  size: number,
  now: number,
): string | null {
  if (!Number.isFinite(size) || size < MIN_CREW || size > MAX_CREW) return `a crew is ${MIN_CREW} to ${MAX_CREW} players`;
  const seats = state?.seats ?? [];
  const claimed = seats.filter((s) => s.claimed === true || (typeof s.token === 'string' && s.token !== '')).map((s) => s.seat);
  const highest = claimed.length > 0 ? Math.max(...claimed) : 0;
  if (size < highest) return `seat ${highest} is already aboard`;
  const held = seats.filter((s) => seatHeld({ heldAt: s.heldAt ?? null }, now)).map((s) => s.seat);
  const highestHeld = held.length > 0 ? Math.max(...held) : 0;
  if (size < highestHeld) return `seat ${highestHeld} is being checked in at the door`;
  return null;
}
